import React, { useEffect, useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { authService, guestXPHelper, referralService } from '../services/api';
import { applyPublicLanguage, getPreferredPublicLanguage, googleLocaleForPublicLanguage } from '../utils/publicLanguage';
import { clearPostAuthRedirect, resolvePostAuthRedirect } from '../utils/postAuthRedirect';
import BrandLogo from '../components/BrandLogo';
import './Auth.css';

function getEffectiveSubscriptionTier(user = {}) {
  if (user.role === 'admin') return 'pro';
  return user.subscriptionTier || 'plus';
}

function isProOrUltraTier(tier) {
  return ['pro', 'ultra'].includes(String(tier || '').toLowerCase());
}

function getEffectiveAiEntitlements(user = {}) {
  if (user.role === 'admin') {
    return {
      ...(user.aiEntitlements || {}),
      subscriptionTier: 'pro',
      canUseAI: true,
      canSyncAIMemory: true,
      canUsePracticeContext: true,
      aiMemoryScope: 'cloud',
    };
  }
  if (user.aiEntitlements) return user.aiEntitlements;

  const subscriptionTier = getEffectiveSubscriptionTier(user);
  const hasCloudFeatures = isProOrUltraTier(subscriptionTier);
  return {
    subscriptionTier,
    canUseAI: true,
    canSyncAIMemory: hasCloudFeatures,
    canUsePracticeContext: hasCloudFeatures,
    aiMemoryScope: hasCloudFeatures ? 'cloud' : subscriptionTier === 'plus' ? 'device' : 'none',
  };
}

function RegisterPage({ setIsAuthenticated, setIsGuest, setEmailVerified }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const postAuthRedirect = resolvePostAuthRedirect(window.location.search);

  // Languages should already be set by LanguageSelectPage
  const nativeLanguage = localStorage.getItem('nativeLanguage');
  const targetLanguage = localStorage.getItem('targetLanguage');
  const pairConfirmedForRegistration = localStorage.getItem('languagePairConfirmed') === 'true';

  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    applyPublicLanguage(i18n);
  }, [i18n]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const guestXP = guestXPHelper.get();
      const nativeLang = localStorage.getItem('nativeLanguage') || '';
      const targetLang = localStorage.getItem('targetLanguage') || '';
      const response = await authService.googleLogin(
        credentialResponse.credential,
        guestXP,
        nativeLang,
        targetLang
      );
      const data = response.data;
      const user = data.user;
      localStorage.setItem('token', data.token);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('username', user.username);
      localStorage.setItem('userFullName', user.fullName || '');
      localStorage.setItem('userEmail', user.email || '');
      localStorage.setItem('userRole', user.role || 'user');
      localStorage.setItem('subscriptionTier', getEffectiveSubscriptionTier(user));
      localStorage.setItem('aiEntitlements', JSON.stringify(getEffectiveAiEntitlements(user)));
      localStorage.setItem('emailVerified', String(!!user.emailVerified));
      localStorage.removeItem('guestMode');
      guestXPHelper.clear();
      setIsGuest(false);
      setIsAuthenticated(true);
      setEmailVerified(!!user.emailVerified);
      // Credit the campaign link this new user arrived through, if any.
      if (data.isNewUser) {
        const refCode = localStorage.getItem('referralCode');
        if (refCode) {
          referralService.attributeSignup(refCode).catch(() => {});
          localStorage.removeItem('referralCode');
        }
      }
      // Sign-in always succeeds; only where we land next depends on whether
      // the account has a language pair. Normally the picker that guards this
      // page already supplied one, so no second pick is needed.
      if (!user.languageSetupComplete) {
        localStorage.setItem('needsLanguageSetup', 'true');
        const params = new URLSearchParams({ mode: 'google-setup' });
        if (postAuthRedirect) params.set('redirect', postAuthRedirect);
        navigate(`/select-language?${params.toString()}`);
        return;
      }
      localStorage.setItem('nativeLanguage', user.nativeLanguage || '');
      localStorage.setItem('targetLanguage', user.targetLanguage || '');
      localStorage.removeItem('needsLanguageSetup');
      navigate(postAuthRedirect || '/');
      if (postAuthRedirect) clearPostAuthRedirect(postAuthRedirect);
    } catch (err) {
      setError(err.response?.data?.message || t('login.googleFailed', "Google sign-in failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // Registration is normally entered through LanguageSelectPage. Keep direct
  // links and stale browser state from creating a user without a complete,
  // explicit language pair. This never blocks sign-up: the picker is always
  // completable and returns here (with any funnel params preserved).
  if (!pairConfirmedForRegistration || !nativeLanguage || !targetLanguage || nativeLanguage === targetLanguage) {
    const passthrough = new URLSearchParams(window.location.search);
    passthrough.set('mode', 'register');
    return <Navigate to={`/select-language?${passthrough.toString()}`} />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const emailTouched = formData.email.length > 0;
  const emailValid = EMAIL_REGEX.test(formData.email);

  const confirmTouched = formData.confirmPassword.length > 0;
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordsMatch) {
      setError(t('register.passwordsNoMatch', "Passwords do not match"));
      return;
    }

    setLoading(true);

    try {
      const guestXP = guestXPHelper.get();
      const response = await authService.register(
        formData.username,
        formData.email,
        formData.password,
        guestXP,
        nativeLanguage || '',
        targetLanguage || '',
        formData.fullName
      );
      const data = response.data;
      const user = data.user;
      localStorage.setItem('token', data.token);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('username', user.username);
      localStorage.setItem('userFullName', user.fullName || '');
      localStorage.setItem('userEmail', user.email || '');
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('subscriptionTier', getEffectiveSubscriptionTier(user));
      localStorage.setItem('aiEntitlements', JSON.stringify(getEffectiveAiEntitlements(user)));
      localStorage.setItem('nativeLanguage', user.nativeLanguage || '');
      localStorage.setItem('targetLanguage', user.targetLanguage || '');
      localStorage.setItem('emailVerified', String(!!user.emailVerified));
      localStorage.removeItem('guestMode');
      // Pair was sent with registration, so any setup flag left behind by an
      // abandoned Google attempt must not bounce this account to the picker.
      localStorage.removeItem('needsLanguageSetup');
      guestXPHelper.clear();
      setIsAuthenticated(true);
      setIsGuest(false);
      setEmailVerified(!!user.emailVerified);
      // Credit the campaign link this new user arrived through, if any.
      const refCode = localStorage.getItem('referralCode');
      if (refCode) {
        referralService.attributeSignup(refCode).catch(() => {});
        localStorage.removeItem('referralCode');
      }
      // Resume a pending action (e.g. cohort registration) if one was started.
      navigate(postAuthRedirect || '/');
      if (postAuthRedirect) clearPostAuthRedirect(postAuthRedirect);
    } catch (err) {
      setError(
        err.response?.data?.code === 'FULL_NAME_INVALID'
          ? t('levelTests.fullNameInvalid', 'Enter at least two characters for your full name.')
          : err.response?.data?.message || t('register.registrationFailed', "Registration failed")
      );
    } finally {
      setLoading(false);
    }
  };

  const loginParams = new URLSearchParams();
  if (postAuthRedirect) loginParams.set('redirect', postAuthRedirect);
  const loginPath = loginParams.size ? `/login?${loginParams.toString()}` : '/login';

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button
          type="button"
          className="auth-back-btn"
          onClick={() => navigate('/select-language?mode=register')}
          aria-label={t('common.back', 'Back')}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <BrandLogo variant="lockup" className="auth-logo" decorative={false} />
        <h1>{t('register.title', "Create Your Account")}</h1>

        {error && <div className="error">{error}</div>}

        <div className="google-btn-wrapper">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError(t('login.googleFailed', "Google sign-in failed. Please try again."))}
            theme="outline"
            size="large"
            width="340"
            text="signup_with"
            shape="pill"
            locale={googleLocaleForPublicLanguage(getPreferredPublicLanguage())}
            use_fedcm_for_button={false}
          />
        </div>
        <div className="auth-divider"><span>{t('common.or', 'or')}</span></div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">{t('register.username', "Username")}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={t('register.usernamePlaceholder', "Choose a username")}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="fullName">{t('register.fullNameOptional', 'Full name (optional)')}</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder={t('register.fullNamePlaceholder', 'Name to show on certificates')}
              autoComplete="name"
            />
            <p className="auth-field-hint">
              {t('register.fullNameHint', 'You can add this now or before issuing a certificate.')}
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="email">{t('register.email', "Email")}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('register.emailPlaceholder', "Enter your email")}
              required
            />
            {emailTouched && (
              <p style={{
                marginTop: '6px',
                fontSize: '0.85rem',
                color: emailValid ? '#16a34a' : '#dc2626',
              }}>
                {emailValid ? `✓ ${t('register.validEmail', "Valid email address")}` : `✗ ${t('register.invalidEmail', "Please enter a valid email address")}`}
              </p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="password">{t('register.password', "Password")}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('register.passwordPlaceholder', "Create a password (min. 6 characters)")}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('login.hidePassword', "Hide password") : t('login.showPassword', "Show password")}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('register.confirmPassword', "Confirm Password")}</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('register.confirmPlaceholder', "Re-enter your password")}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? t('login.hidePassword', "Hide password") : t('login.showPassword', "Show password")}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {confirmTouched && (
              <p style={{
                marginTop: '6px',
                fontSize: '0.85rem',
                color: passwordsMatch ? '#16a34a' : '#dc2626',
              }}>
                {passwordsMatch ? `✓ ${t('register.passwordsMatch', "Passwords match")}` : `✗ ${t('register.passwordsNoMatch', "Passwords do not match")}`}
              </p>
            )}
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !emailValid || (confirmTouched && !passwordsMatch)}
          >
            {loading ? t('register.creatingAccount', "Creating account...") : t('register.signUpButton', "Sign Up")}
          </button>
        </form>

        <p className="auth-link">
          {t('register.hasAccount', "Already have an account?")} <Link to={loginPath}>{t('register.loginHere', "Login here")}</Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterPage;
